import { useCallback, useState } from 'react';

/**
 * A set of recommendation ids that belongs to ONE client.
 *
 * A search-term recommendation id is sha1(search_term|category|negative): it
 * names no client, so two clients' reviews can hold the same id ("diy fence
 * kit" is a DIY term in both). A plain Set kept across a client switch let a
 * row ticked for one client stay ticked, and be applied, for the next one
 * when its review was cached. Here the ids carry the client they were picked
 * for: read for any other client they are empty, and an update made for one
 * client never adds to another's.
 */
export type IdSetUpdate = Set<string> | ((prev: Set<string>) => Set<string>);

const NO_IDS: ReadonlySet<string> = new Set<string>();

interface ScopedIds {
  tenantId: string;
  ids: Set<string>;
}

export function useTenantScopedIdSet(
  tenantId: string,
): [ReadonlySet<string>, (update: IdSetUpdate) => void] {
  const [scoped, setScoped] = useState<ScopedIds>(() => ({
    tenantId,
    ids: new Set<string>(),
  }));
  const ids = scoped.tenantId === tenantId ? scoped.ids : NO_IDS;
  const setIds = useCallback(
    (update: IdSetUpdate) => {
      setScoped((prev) => {
        const base = prev.tenantId === tenantId ? prev.ids : new Set<string>();
        const next = typeof update === 'function' ? update(base) : update;
        if (prev.tenantId === tenantId && next === prev.ids) return prev;
        return { tenantId, ids: next };
      });
    },
    [tenantId],
  );
  return [ids, setIds];
}
